<template>
	<div class="smart__audit">
		<div
			v-if="vm.isLoading.value"
			class="smart__audit__placeholder"
		>
			<Loading style="height: 25px; width: 25px" />
		</div>

		<div
			v-else-if="vm.hasFailed.value"
			class="smart__audit__placeholder"
		>
			<label>{{ $t('falhaAoCarregarAuditoriaSmart') }}</label>
		</div>

		<div
			v-else-if="!audit"
			class="smart__audit__placeholder"
		>
			<label>{{ $t('semDadosDeAuditoriaSmart') }}</label>
		</div>

		<template v-else>
			<h2 class="smart__audit__title">
				{{ $t('modelEquation') }}
				<i
					class="far fa-copy smart__audit__copy"
					:title="$t('copiarEquacao').toString()"
					@click="copiarEquacao"
				/>
			</h2>

			<vue-mathjax
				class="smart__audit__formula"
				:formula="audit.adjustedModelLatex"
			/>

			<div class="smart__audit__betas">
				<v-chip
					v-for="(coefficient, i) in audit.adjustedModelCoefficients"
					:key="audit.coefficientLabels[i]"
					small
					:title="$t('copiarBeta').toString()"
					@click="copiarBeta(coefficient)"
				>
					{{ audit.coefficientLabels[i] }} = {{ coefficient }}
				</v-chip>
				<v-chip
					small
					color="#95bb49"
					@click="copiarTodosOsBetas"
				>
					{{ $t('copiarTodos') }}
				</v-chip>
			</div>

			<div class="smart__audit__kpis">
				<KPI
					:value="audit.formattedErrorPercentage"
					:raw-value="String(audit.errorPercentage)"
					:description="`${$t('error')} (%)`"
				/>
				<KPI
					:value="audit.formattedError"
					:raw-value="String(audit.error)"
					:description="$t('error').toString()"
				/>
				<KPI
					:value="audit.formattedAdjustedR2"
					:raw-value="String(audit.adjustedR2)"
					:description="$t('adjustedR2').toString()"
				/>
			</div>

			<!--
				Sem rotulo proprio: o PNG do backend ja vem com o titulo de cada
				grafico desenhado dentro ('Real vs Estimado' e 'Residuos'), entao um
				<h3> aqui duplicava a informacao.
			-->
			<div class="smart__audit__chart">
				<img
					v-if="audit.hasResiduesChart && !graficoIndisponivel"
					:src="audit.residuesChartUrl"
					:alt="$t('residuals').toString()"
					@error="graficoIndisponivel = true"
				/>
				<label
					v-else
					class="smart__audit__chart__empty"
				>
					{{ $t('graficoDeResiduosIndisponivel') }}
				</label>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import KPI from '../../../../components/KPI/KPI.vue'
import Loading from '../../../../components/Loading/Loading.vue'
import { SmartProcessingAuditViewModel } from '../models/SmartProcessingAudit.viewmodel'
import { useMessages } from '../../../../composables/useMessages'
import i18n from '../../../../VueI18n'

const props = defineProps<{ viewModel: SmartProcessingAuditViewModel }>()

const vm = computed(() => props.viewModel)
const audit = computed(() => props.viewModel.selectedAudit.value)

const graficoIndisponivel = ref(false)
const { addMessage } = useMessages()

// O PNG e por variavel: trocar de variavel tem que dar nova chance ao <img>,
// senao o primeiro erro de carregamento esconderia todos os graficos seguintes.
watch(
	() => props.viewModel.selectedInterestVariable.value,
	() => {
		graficoIndisponivel.value = false
	},
)

function avisaCopia(description: string) {
	addMessage({
		title: i18n.t('sucesso') as string,
		description,
		icon: 'far fa-check-circle',
		type: 'success',
	})
}

function copiarEquacao() {
	if (!audit.value) return

	navigator.clipboard.writeText(audit.value.adjustedModelLatex)
	avisaCopia(i18n.t('equacaoCopiadaParaAreaDeTransferencia') as string)
}

function copiarBeta(coefficient: number) {
	navigator.clipboard.writeText(String(coefficient))
	avisaCopia(i18n.t('betaCopiadoParaAreaDeTransferencia') as string)
}

/**
 * TSV com virgula decimal para colar direto no Excel pt-BR -- mesmo formato que
 * o "Copiar Todos" do AboutModel dos modelos hipsometricos entrega.
 */
function copiarTodosOsBetas() {
	if (!audit.value) return

	const linhas = audit.value.adjustedModelCoefficients.map(
		(coefficient, i) =>
			`${audit.value?.coefficientLabels[i]}\t${String(coefficient).replace('.', ',')}`,
	)

	navigator.clipboard.writeText(linhas.join('\n'))
	avisaCopia(i18n.t('betasCopiadosParaAreaDeTransferencia') as string)
}
</script>

<style scoped>
/*
 * `flex: 1` + `min-height: 0` em vez de `height: 100%`: como item do
 * .modeling__preview, sem isto o painel crescia com o conteudo e era cortado
 * pelo card em vez de rolar por dentro.
 */
.smart__audit {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	padding: 1rem;
	gap: 0.5rem;
	overflow: auto;
}

.smart__audit__placeholder {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	gap: 0.5rem;
	font-size: 1rem;
	color: var(--color-gray);
	text-align: center;
}

.smart__audit__title {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	margin: 0;
	font-size: 1.25rem;
	font-weight: 700;
	color: rgb(var(--color-primary));
}

.smart__audit__copy {
	cursor: pointer;
	font-size: 0.9rem;
	color: var(--color-gray);
}

.smart__audit__copy:hover {
	color: rgb(var(--color-primary));
}

/*
 * O padding vertical nao e estetico: o vue-mathjax chama MathJax.Hub.Config com
 * `'HTML-CSS': { styles: { '.MathJax_Display': { margin: 0 } } }`, o que zera a
 * margem que o display math usa para acomodar a propria altura. Sem folga aqui,
 * a tinta de um rac (numerador e barra) escapa da caixa do elemento e invade
 * o titulo acima e os chips de beta abaixo -- e o que acontecia nas equacoes com
 * razao de bandas (RED/BLUE).
 *
 * Nao usar overflow-wrap para equacao longa: a saida do MathJax nao e texto
 * quebravel, e o proprio `linebreaks: { automatic: true }` da mesma config ja
 * quebra o display math na largura do container.
 */
.smart__audit__formula {
	display: block;
	max-width: 100%;
	text-align: center;
	line-height: normal;
	padding: 0.75rem 0;
}

.smart__audit__betas {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 0.25rem;
}

.smart__audit__kpis {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: space-evenly;
	font-family: 'Open Sans', sans-serif;
}

.smart__audit__chart {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.25rem;
}

.smart__audit__chart__empty {
	font-size: 0.875rem;
	color: var(--color-gray);
}

/*
 * O PNG vem do backend em tamanho arbitrario (900x650 nos casos vistos). Sem
 * teto de altura ele dominava o painel e empurrava o resto para fora da tela.
 */
.smart__audit__chart img {
	max-width: 100%;
	max-height: 38vh;
	width: auto;
	height: auto;
	object-fit: contain;
}
</style>
